<template>
    <v-container fluid class="ma-0 pa-0 login-bg">
        <forgotPasswordSVG :class="[this.$vuetify.breakpoint.smAndDown ? 'loginSVG-mobile' : 'loginSVG']"
                           :rectColor="loginSVGColors.rectColor"
                           :stopColor1="loginSVGColors.stopColor1"
                           :stopColor2="loginSVGColors.stopColor2"
                           :stopColor3="loginSVGColors.stopColor3"
                           :circleColor1="loginSVGColors.circleColor1"
                           :circleColor2="loginSVGColors.circleColor2">

        </forgotPasswordSVG>

        <v-row>
            <v-toolbar flat>
                <v-hover v-slot="{hover}">
                    <v-btn @click="toggleLocale" class="text-none text-normal" text absolute right plain :color="hover ? 'linkHover' : 'primary'">{{$t('reset-password.locale')}}</v-btn>
                </v-hover>
            </v-toolbar>
        </v-row>
        <v-row>
            <v-col>
                <v-row align="center" justify="center" :class="isMobile ? [] : ['py-16', 'mt-16']">
                    <v-col align="center">
                        <v-card class="my-auto rounded-xl"
                                v-bind:class="isMobile ? ['pt-4'] : ['pt-8']"
                                max-width="500px"
                                flat>
                            <v-img class="mx-auto px-5 login-logo"
                                   :src="`../../assets/Branding/${this.$experience.key}/Images/${getLogo}`"
                                   max-width="270px"
                                   contain>
                            </v-img>

                            <v-card-subtitle class="text-center card-title">{{$t('reset-password.title')}}</v-card-subtitle>
                            <v-card-text class="pb-0">
                                <v-row>
                                    <v-col class="pa-0 pt-5" v-bind:class="[ this.$vuetify.breakpoint.smAndDown ? 'mx-9' : 'mx-10' ]">
                                        <v-alert v-if="this.errorMsg" dense dark type="error">{{$t(this.errorMsg)}}</v-alert>
                                        <v-form ref="resetForm" v-model="resetValid" @submit.prevent="onConfirm" lazy-validation>
                                            <v-text-field v-model="resetPasswordViewModel.userName"
                                                          :placeholder="$t('reset-password.username')"
                                                          outlined
                                                          hide-details
                                                          maxlength="50"
                                                          class="mb-1 text-normal min-height-1"
                                                          :rules="usernameRules"
                                                          prepend-inner-icon="mdi-account-outline"></v-text-field>

                                            <v-text-field v-model="resetPasswordViewModel.password"
                                                          :placeholder="$t('reset-password.newPassword')"
                                                          :type="passwordVisible ? 'text' : 'password'"
                                                          outlined
                                                          hide-details
                                                          required
                                                          class="text-normal min-height-1 mb-2"
                                                          maxlength="30"
                                                          :rules="passwordRules"
                                                          :append-icon="passwordVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                                                          @click:append="passwordVisible = !passwordVisible"></v-text-field>

                                            <v-text-field v-model="resetPasswordViewModel.confirmPassword"
                                                          :label="$t('reset-password.passwordConfirmation')"
                                                          :type="passwordConfirmationVisible ? 'text' : 'password'"
                                                          outlined
                                                          hide-details
                                                          required
                                                          maxlength="30"
                                                          class="text-normal min-height-1 mb-2"
                                                          :rules="passwordConfirmationRules"
                                                          :append-icon="passwordConfirmationVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                                                          @click:append="passwordConfirmationVisible = !passwordConfirmationVisible"></v-text-field>
                                        </v-form>

                                        <v-row class="my-1 px-3">
                                            <span class="text-small font-weight-bold">{{$t('register.bottomTextBold')}}</span>
                                            <span class="text-small text-left">{{$t('register.bottomText')}}</span>
                                        </v-row>

                                        <v-row class="mb-2">
                                            <v-hover v-slot="{hover}">
                                                <v-btn class="text-none text-small min-height-1" type="submit" text plain
                                                       :color="hover ? (loginLinkHoverColor ? loginLinkHoverColor : 'linkHover') : (loginLinkColor ? loginLinkColor : 'primary')"
                                                       to="/login">{{$t('reset-password.returnToLogin')}}</v-btn>
                                            </v-hover>
                                        </v-row>
                                    </v-col>
                                </v-row>
                            </v-card-text>
                            <v-card-actions class="mx-8">
                                <v-hover v-slot="{hover}">
                                    <v-btn block
                                           :disabled="isFormProcessing"
                                           depressed
                                           :dark="!isFormProcessing"
                                           class="text-none mb-8 text-normal min-height-1"
                                           :color="hover ? 'buttonHover' : 'primary'"
                                           @click="onConfirm">
                                        {{$t('reset-password.send')}}
                                        &nbsp;
                                        <v-progress-circular v-if="isFormProcessing" indeterminate
                                                             color="primary">
                                        </v-progress-circular>
                                    </v-btn>
                                </v-hover>
                            </v-card-actions>
                        </v-card>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-dialog v-model="passwordResetConfirmation" max-width="615px">
            <v-card class="pa-8 rounded-xl" flat>
                <v-row align="center" justify="center">
                    <v-icon size="96px"
                            color="#74DF13"
                            class="my-5">mdi-check-circle-outline</v-icon>
                </v-row>
                <v-card-subtitle class="text-center card-subtitle font-weight-medium mt-1">{{ $t('reset-password.title') }}</v-card-subtitle>
                <v-card-subtitle class="text-center card-subtitle2 font-weight-medium mt-1">{{ $t('reset-password.passwordReseted') }}</v-card-subtitle>
                <v-card-actions>
                    <v-row justify="center">
                        <v-btn class="text-normal" depressed color="primary" x-large @click="onPasswordResetConfirmation">{{$t('ok')}}</v-btn>
                    </v-row>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
    import { apiService } from "../../services";
    import { mapState, mapActions } from "vuex";
    import forgotPasswordSVG from '../../components/forgotPasswordSVG.vue';
    import {
        passwordRules,
        passwordConfirmationRules,
        usernameRules
    } from '../../utils/validationRules';

    export default {
        name: "resetPassword",
        components: { forgotPasswordSVG },
        data() {
            return {
                resetValid: false,
                resetPasswordViewModel: {
                    userId: undefined,
                    username: undefined,
                    token: undefined,
                    password: undefined,
                    confirmPassword: undefined
                },
                errorMsg: "",
                requiredRules: [
                    v => !!v || this.$t('validation.fieldRequired')
                ],
                // Enhanced validation rules for WCAG AA (3.3.3)
                passwordRules: passwordRules(this.$t.bind(this)),
                usernameRules: usernameRules(this.$t.bind(this)),
                logo: "",
                formProcess: false,
                passwordConfirmationVisible: false,
                passwordVisible: false,
                passwordResetConfirmation: false,
            }
        },
        computed: {
            ...mapState("account", ["status", "user"]),

            isMobile() {
                return this.$vuetify.breakpoint.smAndDown;
            },
            formMaxWidth() {
                if (this.step === 2) return "600px";
                return "375px";
            },
            loginSVGColors() {
                return this.$vuetify.theme.themes.light.loginSVG;
            },
            isFormProcessing() {
                return this.formProcess;
            },
            loginLinkHoverColor() {
                return this.$experience.loginLinkHoverColor;
            },
            loginLinkColor() {
                return this.$experience.loginLinkColor;
            },
            getLogo() {
                return (this.$i18n.locale == 'en' && this.$experience.logoLoginEN != null) ? this.$experience.logoLoginEN : this.$experience.logo;
            },
            // Computed password confirmation rules (needs dynamic access to password)
            passwordConfirmationRules() {
                return passwordConfirmationRules(this.$t.bind(this), this.resetPasswordViewModel.password);
            }

        },
        created() {
            this.logout();

            let params = globalThis.location.href.substr(globalThis.location.href.indexOf('?'))
            let param = new URLSearchParams(params);

            if (param.has('token')) {
                this.resetPasswordViewModel.token = param.get('token')
            }
            else {
                this.$router.push("login");
            }

            if (this.resetPasswordViewModel.token != null) {
                apiService.validateToken(this.resetPasswordViewModel)
                    .then((response) => {
                        console.log(response);
                    })
                    .catch((error) => {
                        console.log(error);
                        this.$router.push("login");
                    });
            }
        },
        mounted() {
            let unsubscribe = null
            unsubscribe = this.$store.subscribe(({ type }) => {
                if (type === 'account/loginSuccess') {
                    unsubscribe() // So it only reacts once.
                }
            })
        },
        methods: {
            ...mapActions("account", ["login", "logout"]),
            toggleLocale() {
                this.$emit("toggleLocale");
            },
            fieldRequired(val) {
                return this.$t('reset-password.fieldRequired', { field: this.$t(val) });
            },
            onConfirm() {
                this.errorMsg = "";
                this.$refs.resetForm.validate();

                let params = globalThis.location.href.substr(globalThis.location.href.indexOf('?'))
                let param = new URLSearchParams(params);

                if (param.has('token')) {
                    this.resetPasswordViewModel.token = param.get('token')
                }

                if (this.resetValid && this.resetPasswordViewModel.token != null) {
                    this.formProcess = true;

                    apiService.resetPassword(this.resetPasswordViewModel)
                        .then((response) => {
                            if (response.data.success) {
                                this.passwordResetConfirmation = true;
                                localStorage.setItem("user", null);
                            }
                        })
                        .catch((error) => {
                            this.formProcess = false;
                            this.errorMsg = error;
                        });
                }
                
            },
            onPasswordResetConfirmation() {
                this.passwordResetConfirmation = false;
                this.$router.push("login");
            },
            textLengthRule(fieldName, n) {
                return this.$t('reset-password.textLengthRule', { field: this.$t(fieldName), n: n })
            },
        },
    }

</script>

<style scoped>
    .login-logo {
        margin-top: 50px !important;
        margin-bottom: 50px !important;
    }

    .loginSVG {
        position: absolute;
        top: 50%;
        width: 100%;
        height: 50%;
    }

    .loginSVG-mobile {
        position: absolute;
        top: 48px;
        width: 100%;
        height: 50%;
    }
</style>

